import { v4 as generateUUID } from 'uuid';
import {
  getAllUserOrgs,
  getUserOrgRole,
  isOrgOwner,
  getUserOwnedOrgs,
  getUserMemberships,
} from './orgMembership';

/**
 * Returns array of enhanced org profiles the user has any relationship with.
 * Each entry: { id, name, profileImage, role, isOwner, joinedAt?, ...orgBranding }
 *
 * @param {object} user - The user object.
 * @param {array} invitedOrganizationProfiles - Legacy fallback list of invited
 *   organization profiles (still used by ducks/organizations.duck during the
 *   transition). Merged in and deduped by id; spec entries win on conflict.
 * @returns {array}
 */
export const getUserOrganizationProfiles = (user, invitedOrganizationProfiles = []) => {
  if (!user) {
    if (!Array.isArray(invitedOrganizationProfiles)) return [];
    return invitedOrganizationProfiles.filter(p => p && p.id);
  }

  const specOrgs = getAllUserOrgs(user);
  const seen = new Set(specOrgs.map(o => o.id));

  const extraInvited = Array.isArray(invitedOrganizationProfiles)
    ? invitedOrganizationProfiles.filter(p => {
        if (!p || !p.id) return false;
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      })
    : [];

  return [...specOrgs, ...extraInvited];
};

/**
 * Get the selected organization from the list of organizations based on the
 * provided organization ID. If `user` is provided, enriches the result with
 * the user's role + isOwner.
 *
 * @param {Array} organizations - List of organizations to search through.
 * @param {string} organizationId - ID of the organization to find.
 * @param {object} user - User object (for enhanced organization data).
 *
 * @return {Object|null} The selected organization with enhanced data, or null.
 */
export const getSelectedOrganizationProfile = (organizations, organizationId, user = null) => {
  if (!organizations || organizations.length === 0) {
    return null;
  }

  let selectedOrg;
  if (organizationId) {
    selectedOrg = organizations.find(org => org.id === organizationId) || organizations[0];
  } else {
    selectedOrg = organizations[0];
  }

  if (!selectedOrg || !user) {
    return selectedOrg;
  }

  const role = getUserOrgRole(user, selectedOrg.id);
  return {
    ...selectedOrg,
    role: role || selectedOrg.role || null,
    isOwner: isOrgOwner(user, selectedOrg.id),
  };
};

/**
 * Best-guess "what org should we default to?" — used by code that runs
 * before URL routing is available.
 *
 * Order:
 *   1. First owned org from publicData.organizationProfiles[]
 *   2. First membership from privateData.memberOfOrganizations[]
 *   3. null
 */
export const getSelectedOrganizationProfileIdFromExtendedData = user => {
  if (!user) return null;

  const owned = getUserOwnedOrgs(user);
  if (owned.length > 0 && owned[0]?.id) {
    return owned[0].id;
  }

  const memberships = getUserMemberships(user);
  if (memberships.length > 0 && memberships[0]?.orgId) {
    return memberships[0].orgId;
  }

  return null;
};

/**
 * Creates a default organization profile during signup.
 *
 * @param {string|null} organizationName - Explicit organization name (preferred).
 * @param {string} userName - User's name for fallback default org name.
 * @returns {object} { publicDataProfiles: [orgProfile] } — no metadata key.
 */
/**
 * publicData.organizationProfileIds: the ids of the organizations a user owns,
 * written next to organizationProfiles. It is the search index the server uses
 * to find an org's owner (server/api-util/orgOwner.js), since a search schema
 * cannot reach inside organizationProfiles.
 */
export const ownedOrganizationIds = organizationProfiles =>
  (organizationProfiles || []).filter(p => p?.isOwner === true && p?.id).map(p => p.id);

export const createDefaultOrganizationProfile = (organizationName = null, userName = 'User') => {
  const orgName = organizationName || `${userName}'s Organization`;
  const orgProfile = {
    id: generateUUID(),
    name: orgName,
    description: organizationName ? '' : 'Default organization created during signup',
    profileImage: null,
    isOwner: true,
    createdAt: new Date().toISOString(),
  };

  return {
    publicDataProfiles: [orgProfile],
  };
};

/**
 * Prepares organization profiles before saving to publicData.
 * Uploads any pending images and strips fields that don't belong in publicData.
 *
 * @param {array} organizationProfiles - Organization profiles to prepare.
 * @param {function} onUploadImage - Image upload function.
 * @param {object} user - Current user object (unused; kept for signature stability).
 *
 * @returns {Promise<object>} { publicDataProfiles: array, metadataUpdates: {} }.
 *   `metadataUpdates` is always an empty object — metadata writes have moved
 *   elsewhere in the new spec.
 */
// eslint-disable-next-line no-unused-vars
export const prepareUpdatedOrganizationProfiles = async (
  organizationProfiles,
  onUploadImage,
  user
) => {
  if (!organizationProfiles || !Array.isArray(organizationProfiles)) {
    console.warn(
      'prepareUpdatedOrganizationProfiles: organizationProfiles is not an array:',
      organizationProfiles
    );
    return { publicDataProfiles: [], metadataUpdates: {} };
  }

  const filteredOrganizationProfiles = organizationProfiles.filter(
    p => p !== null && typeof p !== 'undefined'
  );

  if (filteredOrganizationProfiles.length === 0) {
    return { publicDataProfiles: [], metadataUpdates: {} };
  }

  let uploadFailed = false;
  const preparedPublicDataProfiles = filteredOrganizationProfiles.map(async p => {
    const profileImage = p.profileImage;
    const uploadedImage = profileImage?.lastModified
      ? await onUploadImage(profileImage)
      : profileImage;
    // The upload thunk resolves null on failure; the caller must not save
    // in that case or the stored picture is silently replaced by nothing.
    if (profileImage?.lastModified && !uploadedImage) uploadFailed = true;

    const profileData = {
      ...p,
      id: p.id || generateUUID(),
    };

    if (profileImage && uploadedImage) {
      profileData.profileImage = uploadedImage;
    }

    // Strip fields that don't belong in publicData.
    const { role, subscription, permissions, isOwner, ...publicDataProfile } = profileData;

    return publicDataProfile;
  });

  const publicDataProfiles = await Promise.all(preparedPublicDataProfiles);

  return { publicDataProfiles, metadataUpdates: {}, uploadFailed };
};
